import { SupportError } from './types.js'
import type { SupportApi, SupportHost, SupportState, SupportStartOptions, SupportRecordingInfo, SupportReportOptions, SupportReport, SupportEvent } from './types.js'
import { recorder } from './recorder.js'
import type { RecorderDriver } from './recorder.js'
import { RecordingStore } from './store.js'
import type { Draft } from './store.js'
import { bytes, clone, sanitizeEvent, parseReport, serializeReport, MAX_REPORT_BYTES } from './report.js'
import { createPlayback } from './playback.js'

// Keep enough room for report metadata and annotations while allowing full-page
// snapshots from real Aeppic workspaces to fit into the recording budget.
const MAX_RECORDING_BYTES = MAX_REPORT_BYTES - 1024 * 1024

export function createSupport(host: SupportHost): SupportApi { return new Support(host) }
export class Support implements SupportApi {
  readonly available = true
  private status: SupportState = { phase: 'idle', recordingId: null, startedAt: null, elapsedMs: 0, mode: null, error: null }
  private listeners = new Set<(state: Readonly<SupportState>) => void>()
  private store = new RecordingStore()
  private recovered = new Map<string, Draft>()
  private current: Draft | null = null
  private options: SupportStartOptions | null = null
  private stopRecorder: (() => void) | null = null
  private timer: ReturnType<typeof setInterval> | null = null
  private starting: Promise<SupportRecordingInfo> | null = null
  private stopping: Promise<SupportRecordingInfo | null> | null = null
  private persisted: Promise<void> | null = null
  private destroyed = false
  private byteCount = 0
  private nextCheckpoint = 0
  private playbacks = new Set<Awaited<ReturnType<typeof createPlayback>>>()
  constructor(private host: SupportHost, private driver: RecorderDriver = recorder) {}
  get state(): Readonly<SupportState> {
    return Object.freeze({ ...this.status, elapsedMs: this.status.startedAt === null ? 0 : Math.max(0, Date.now() - this.status.startedAt), error: this.status.error && { ...this.status.error } })
  }
  subscribe(listener: (state: Readonly<SupportState>) => void) {
    this.listeners.add(listener)
    try { listener(this.state) } catch { /* Subscriber failures cannot interrupt recording. */ }
    return () => { this.listeners.delete(listener) }
  }
  private notify() { for (const listener of this.listeners) try { listener(this.state) } catch {} }
  private assertEnabled() {
    if (this.destroyed) throw new SupportError('Destroyed', 'Support has been destroyed')
    if (!this.host.enabled()) throw new SupportError('Disabled', 'The support runtime feature is disabled')
  }
  start(options: SupportStartOptions): Promise<SupportRecordingInfo> {
    try {
      this.assertEnabled()
      if (this.status.phase !== 'idle') throw new SupportError('AlreadyRecording', 'A recording is already starting, running, or stopping')
      if (!options || options.consent !== true) throw new SupportError('InvalidOptions', 'Explicit recording consent is required')
      const o = { mode: 'manual' as const, historySeconds: 60, maxDurationSeconds: 600, maxBytes: MAX_RECORDING_BYTES, storage: 'memory' as const, ...clone(options) }
      if (!['manual', 'flight'].includes(o.mode) || !['memory', 'indexeddb'].includes(o.storage)) throw new SupportError('InvalidOptions', 'Invalid recording mode or storage')
      for (const [key, min, max] of [['historySeconds', 1, 300], ['maxDurationSeconds', 1, 3600], ['maxBytes', 1024, MAX_RECORDING_BYTES]] as const) {
        if (!Number.isFinite(o[key]) || o[key] < min || o[key] > max) throw new SupportError('InvalidOptions', `Invalid ${key}`)
      }
      if (o.shareSelectors && (!Array.isArray(o.shareSelectors) || o.shareSelectors.length > 50 || o.shareSelectors.some(s => typeof s !== 'string' || !s.trim() || s.length > 500))) throw new SupportError('InvalidOptions', 'Invalid sharing selectors')
      this.status = { phase: 'starting', recordingId: null, startedAt: null, elapsedMs: 0, mode: o.mode, error: null }
      this.options = o
      this.notify()
      this.starting = this.begin(o)
      return this.starting
    } catch (error) { return Promise.reject(error) }
  }
  enableFlightRecorder(options: SupportStartOptions) { return this.start({ ...options, mode: 'flight' }) }
  private async begin(options: SupportStartOptions): Promise<SupportRecordingInfo> {
    try {
      if (options.storage === 'indexeddb') await this.store.open(options.storageScope)
      else await this.store.useMemory()
      this.assertEnabled()
      const now = Date.now()
      this.current = { id: globalThis.crypto.randomUUID(), events: [], endedAt: now, incomplete: false, shared: !!options.shareSelectors?.length, updatedAt: now }
      this.byteCount = 0
      this.status.recordingId = this.current.id
      this.status.startedAt = now
      this.stopRecorder = await this.driver.start(event => this.emit(event), options.shareSelectors || [], () => this.fail('RecordingFailed'))
      this.assertEnabled()
      if (this.current.events.length < 2) {
        if (this.status.error?.code === 'LimitReached') throw new SupportError('LimitReached', 'The initial page snapshot exceeds the recording size limit')
        throw new SupportError('RecordingFailed', 'No initial snapshot was captured')
      }
      this.status.phase = 'recording'
      this.nextCheckpoint = now + 5000
      this.timer = setInterval(() => this.tick(), 1000)
      this.notify()
      return this.info(this.current)
    } catch (error) {
      this.stopRecorder?.(); this.stopRecorder = null
      this.current = null
      this.status = { ...this.status, phase: 'idle', recordingId: null, startedAt: null, mode: null, error: { code: error instanceof SupportError ? error.code : 'RecordingFailed', message: 'Recording could not start' } }
      this.notify()
      throw error instanceof SupportError ? error : new SupportError('RecordingFailed', 'Recording could not start')
    }
  }
  private emit(raw: SupportEvent) {
    if (!this.current || this.destroyed) return
    try {
      const event = sanitizeEvent(raw)
      if (!event) return
      const size = bytes(event)
      if (this.options.mode === 'flight') {
        // Under memory pressure retain the newest independently replayable segment.
        // A busy page may retain less than historySeconds, but must not silently stop.
        while (this.byteCount + size > this.options.maxBytes) {
          const events = this.current.events
          let boundary = events.findIndex((e, i) => i > 0 && e.type === 4 && events[i + 1]?.type === 2)
          if (boundary < 0 && event.type === 2 && events[events.length - 1]?.type === 4) boundary = events.length - 1
          if (boundary <= 0) break
          this.byteCount -= events.splice(0, boundary).reduce((sum, e) => sum + bytes(e), 0)
        }
      }
      if (this.byteCount + size > this.options.maxBytes) { this.fail('LimitReached'); return }
      // A single tab's events must remain ordered even if the system clock changes.
      event.timestamp = Math.max(event.timestamp, this.current.events[this.current.events.length - 1]?.timestamp || 0)
      this.current.events.push(event)
      this.byteCount += size
      this.current.endedAt = event.timestamp
      this.evict()
    } catch { this.fail('RecordingFailed') }
  }
  private evict() {
    if (this.options.mode !== 'flight') return
    const events = this.current.events
    const cutoff = Date.now() - this.options.historySeconds * 1000
    let first = 0
    for (let i = 1; i < events.length; i++) if (events[i].type === 2 && events[i].timestamp <= cutoff && events[i - 1].type === 4) first = i - 1
    if (first) {
      this.byteCount -= events.slice(0, first).reduce((sum, e) => sum + bytes(e), 0)
      events.splice(0, first)
    }
  }
  private tick() {
    if (!this.current || this.status.phase !== 'recording') return
    if (!this.host.enabled()) { this.fail('Disabled'); return }
    if (Date.now() - this.status.startedAt >= this.options.maxDurationSeconds * 1000) { void this.stop().catch(() => {}); return }
    if (this.options.mode === 'flight' && Date.now() >= this.nextCheckpoint) {
      this.nextCheckpoint = Date.now() + 5000
      try { this.driver.checkpoint() } catch { this.fail('RecordingFailed') }
    }
    this.evict()
    this.notify()
    if (!this.persisted && this.current.events.length >= 2) {
      const draft = { ...this.current, incomplete: true, reason: 'Recording interrupted before stop', updatedAt: Date.now() }
      this.persisted = this.store.put(draft).catch(() => this.fail('StorageFailed')).finally(() => { this.persisted = null })
    }
  }
  private fail(code: 'RecordingFailed' | 'StorageFailed' | 'LimitReached' | 'Disabled') {
    if (!this.current || this.current.incomplete) return
    this.current.incomplete = true
    this.current.reason = code
    this.status.error = { code, message: `Recording stopped: ${code}` }
    // rrweb may report an error synchronously while start() is installing observers.
    void Promise.resolve().then(() => this.stop()).catch(() => {})
  }
  stop(): Promise<SupportRecordingInfo | null> {
    if (this.stopping) return this.stopping
    if (this.status.phase === 'idle') return Promise.resolve(null)
    this.stopping = this.finish().finally(() => { this.stopping = null })
    return this.stopping
  }
  private async finish(): Promise<SupportRecordingInfo | null> {
    try { await this.starting } catch { return null }
    const draft = this.current
    if (!draft) return null
    this.status.phase = 'stopping'; this.notify()
    if (this.timer) clearInterval(this.timer)
    this.timer = null
    try {
      this.stopRecorder?.(); this.stopRecorder = null
      const end = Math.max(Date.now(), draft.endedAt)
      draft.events.push({ type: 5, timestamp: end, data: { tag: 'ae-support-end', payload: {} } })
      draft.endedAt = end; draft.updatedAt = end
      await this.persisted
      if (draft.events[0]?.type !== 4 || draft.events[1]?.type !== 2) throw new SupportError('RecordingFailed', 'Initial snapshot was incomplete')
      await this.store.put(draft)
      return this.info(draft)
    } catch (error) {
      draft.incomplete = true; draft.reason = 'StorageFailed'
      this.recovered.set(draft.id, clone(draft))
      this.status.error = { code: 'StorageFailed', message: 'The recording remains available in memory but could not be persisted' }
      throw error instanceof SupportError ? error : new SupportError('StorageFailed', 'The recording could not be saved')
    } finally {
      this.current = null
      this.status = { ...this.status, phase: 'idle', recordingId: null, startedAt: null, mode: null }
      this.notify()
    }
  }
  private info(draft: Draft): SupportRecordingInfo {
    const startedAt = draft.events[0]?.timestamp || draft.endedAt
    return { status: this.current?.id === draft.id && this.status.phase !== 'stopping' ? 'recording' : draft.incomplete ? 'incomplete' : 'complete', id: draft.id, startedAt, endedAt: draft.endedAt, durationMs: draft.endedAt - startedAt, bytes: bytes(draft.events), incomplete: draft.incomplete, reason: draft.reason,
      checkpoints: draft.events.filter(e => e.type === 2).map(e => e.timestamp - startedAt) }
  }
  async openStore(scope: string) {
    this.assertEnabled()
    if (this.status.phase !== 'idle') throw new SupportError('AlreadyRecording', 'Cannot change storage while recording')
    this.recovered.clear()
    await this.store.open(scope)
  }
  async list() {
    this.assertEnabled()
    const drafts = new Map((await this.store.list()).map(d => [d.id, d]))
    for (const [id, draft] of this.recovered) drafts.set(id, draft)
    if (this.current) drafts.set(this.current.id, this.current)
    return [...drafts.values()].map(d => this.info(d))
  }
  private async draft(id: string) {
    this.assertEnabled()
    const draft = this.current?.id === id ? this.current : this.recovered.get(id) || (await this.store.list()).find(d => d.id === id)
    if (!draft) throw new SupportError('NotFound', 'Recording was not found')
    return draft
  }
  async get(id: string) { return this.info(await this.draft(id)) }
  async delete(id: string) {
    this.assertEnabled()
    if (this.current?.id === id) throw new SupportError('AlreadyRecording', 'Stop the recording before deleting it')
    await this.store.delete(id)
    this.recovered.delete(id)
  }
  async clear() {
    try { await this.stop() } finally { this.recovered.clear(); await this.store.clear() }
  }
  async buildReport(id: string, options: SupportReportOptions = {}): Promise<SupportReport> {
    const draft = await this.draft(id)
    if (this.current?.id === id) throw new SupportError('AlreadyRecording', 'Stop the recording before exporting it')
    const origin = draft.events[0].timestamp
    const start = options.trim?.startMs ?? 0
    const end = options.trim?.endMs ?? draft.endedAt - origin
    if (!Number.isFinite(start) || !Number.isFinite(end) || start < 0 || end < start || end > draft.endedAt - origin) throw new SupportError('InvalidOptions', 'Invalid trim interval')
    let index = 0
    if (start !== 0) {
      index = draft.events.findIndex((e, i) => e.type === 2 && e.timestamp === origin + start && draft.events[i - 1]?.type === 4) - 1
      if (index < 0) throw new SupportError('InvalidOptions', 'Trim start must match one of the recording checkpoints')
    }
    const events = clone(draft.events.slice(index).filter(e => e.timestamp <= origin + end))
    if (events[1]?.type !== 2) throw new SupportError('InvalidOptions', 'Trim must include a full snapshot')
    const startedAt = origin + start
    events[0].timestamp = startedAt
    events.push({ type: 5, timestamp: origin + end, data: { tag: 'ae-support-end', payload: {} } })
    return parseReport(JSON.stringify({ format: 'aeppic.support', version: 1, recorder: { name: 'rrweb', version: '2.1.4' }, id, title: options.title || '', description: options.description || '', startedAt,
      durationMs: end - start, incomplete: draft.incomplete, privacy: { sharedSensitiveRegions: draft.shared, assets: 'embedded' }, events, annotations: options.annotations || [] }))
  }
  serializeReport = serializeReport
  parseReport = parseReport
  async createPlayback(options: Parameters<SupportApi['createPlayback']>[0]) {
    this.assertEnabled()
    const playback = await createPlayback(options)
    try { this.assertEnabled() } catch (error) { playback.destroy(); throw error }
    this.playbacks.add(playback)
    const destroy = playback.destroy.bind(playback)
    playback.destroy = () => { destroy(); this.playbacks.delete(playback) }
    return playback
  }
  destroy() {
    this.destroyed = true
    this.stopRecorder?.(); this.stopRecorder = null
    if (this.timer) clearInterval(this.timer)
    this.timer = null
    for (const playback of this.playbacks) playback.destroy()
    this.listeners.clear()
    void this.clear().catch(() => {}).finally(() => this.store.close())
  }
}
